/**
 * Markdown renderer for Copilot chat bubbles, ported verbatim from
 * reactdashboard's CopilotRail/markdown.ts (itself from the rebelboost
 * chat-widget). HTML-escapes first, then applies regex transforms —
 * escape-then-format, no raw HTML passthrough.
 */

function sanitizeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function renderMarkdown(text) {
  let t = sanitizeHtml(String(text == null ? '' : text));

  // Protect code spans/blocks so URLs inside them are not linkified.
  const protectedSpans = [];
  const protect = html => {
    const token = `\x00${protectedSpans.length}\x00`;
    protectedSpans.push(html);
    return token;
  };

  t = t.replace(/```([\s\S]*?)```/g, (_, c) => protect(`<pre>${c.trim()}</pre>`));
  t = t.replace(/`([^`\n]+?)`/g, (_, c) => protect(`<code>${c}</code>`));

  // Markdown links are protected before bare URLs are linkified so their
  // destinations cannot be transformed into nested anchors. Only allow the
  // same http(s) protocols supported for bare links.
  t = t.replace(/\[([^\]\n]+)\]\((https?:\/\/[^\s<>()"']+)\)/g, (_, label, url) =>
    protect(`<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`)
  );

  // Keep invalid Markdown-link candidates as text so a valid URL fragment in
  // a malformed or unsafe destination never becomes a bare link.
  t = t.replace(/\[[^\]\n]+\]\([^\n]*?(?:\)|$)/g, match => protect(match));

  // Bare http(s) URLs → anchors. Runs on escaped text; `*` excluded so
  // adjacent bold/italic markers aren't eaten into the href.
  t = t.replace(/\bhttps?:\/\/[^\s<*]+/g, match => {
    const trailingMatch = match.match(/[.,!?;:)\]}'"*]+$/);
    const trailing = trailingMatch ? trailingMatch[0] : '';
    const url = match.slice(0, match.length - trailing.length);
    return `${protect(
      `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
    )}${trailing}`;
  });

  t = t.replace(/^\s*###\s+(.+)$/gm, '<h3>$1</h3>');
  t = t.replace(/^\s*##\s+(.+)$/gm, '<h2>$1</h2>');
  t = t.replace(/^\s*#\s+(.+)$/gm, '<h1>$1</h1>');

  t = t.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  t = t.replace(/\*([^*\n]+?)\*/g, '<em>$1</em>');

  // Numbered list
  t = t.replace(/(^|\n)((?:\d+\.\s+.+(?:\n|$))+)/g, (_, pre, block) => {
    const items = block
      .trim()
      .split(/\n/)
      .map(l => `<li>${l.replace(/^\d+\.\s+/, '')}</li>`)
      .join('');
    return `${pre}<ol>${items}</ol>`;
  });

  // Bullet list
  t = t.replace(/(^|\n)((?:[-*]\s+.+(?:\n|$))+)/g, (_, pre, block) => {
    const items = block
      .trim()
      .split(/\n/)
      .map(l => `<li>${l.replace(/^[-*]\s+/, '')}</li>`)
      .join('');
    return `${pre}<ul>${items}</ul>`;
  });

  // Paragraphs — leave standalone block tokens (fenced code) unwrapped.
  // eslint-disable-next-line no-control-regex
  const STANDALONE_TOKEN_RE = /^\x00(\d+)\x00$/;
  const isStandaloneBlock = trimmed => {
    const m = trimmed.match(STANDALONE_TOKEN_RE);
    return m != null && /^<pre/.test(protectedSpans[Number(m[1])] || '');
  };
  t = t
    .split(/\n{2,}/)
    .map(p => {
      const trimmed = p.trim();
      if (isStandaloneBlock(trimmed) || /^<(h\d|ul|ol|pre|p)/.test(trimmed)) return p;
      return `<p>${p.replace(/\n/g, '<br/>')}</p>`;
    })
    .join('');

  // Restore protected code spans/blocks.
  // eslint-disable-next-line no-control-regex
  t = t.replace(/\x00(\d+)\x00/g, (_, i) => protectedSpans[Number(i)] || '');

  return t;
}
